import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(formData.email, formData.password, formData.name);
    
    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="font-bold text-3xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              RENEW<span className="text-[#00F0FF]">|</span>LABS
            </span>
          </Link>
          <h1 
            className="text-2xl font-bold text-white mt-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Create Account
          </h1>
          <p className="text-[#A1A7B5] mt-2">
            Join the RENEW LABS research community
          </p>
        </div>

        <div className="glass-card p-8">
          {error && (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/50 text-[#FF3B30] px-4 py-3 rounded-lg mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-white font-medium mb-2 block">Full Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="input-field w-full pl-10"
                  required
                  data-testid="register-name-input"
                />
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Email</label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="input-field w-full pl-10"
                  required
                  data-testid="register-email-input"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Password</label>
              <div className="relative">
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="input-field w-full pl-10"
                  required
                  data-testid="register-password-input"
                />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Confirm Password</label>
              <div className="relative">
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className="input-field w-full pl-10"
                  required
                  data-testid="register-confirm-password-input"
                />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Create Account</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A1A7B5]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
